import { createStore, createEffect, createEvent, combine } from 'effector';
import i18n from 'i18n';
import { ApiUtils } from '@shared/utils';
import { UsersApi } from '@shared/api/rest';
import type { AddUserCardsRequest, DeleteUserCardsRequest, UsersRequest, UserResponse } from '@shared/api/rest/users';
import { $notificationStore } from '../notificationStore';
import { type IQueryParams, RequestStrategyEnum } from '@shared/api/models';

const getCardUsersEffect = createEffect((params: IQueryParams<UsersRequest>) => UsersApi.getUsers(params.request, params?.signal));
const addUsersInCardEffect = createEffect((request: AddUserCardsRequest) => UsersApi.addUserCards(request));
const deleteUserInCardEffect = createEffect((params: IQueryParams<DeleteUserCardsRequest>) =>
  UsersApi.deleteUserCards(params.request, params?.signal),
);
const clearEvent = createEvent();

const $dataStore = createStore<UserResponse[]>([])
  .on(getCardUsersEffect.done, (current, data) => data.result.data || current)
  .on(addUsersInCardEffect.done, (current, data) => (data.result.data ? [...current, data.result.data] : current))
  .on(deleteUserInCardEffect.done, (current, data) => {
    const userIds = (data.params.request as DeleteUserCardsRequest).userIds;
    return current.filter((user) => !userIds.includes(user.id));
  })
  .reset(clearEvent);

const $loadingStore = createStore<boolean>(false)
  .on(getCardUsersEffect.pending, (_, data) => data)
  .on(getCardUsersEffect.fail, () => false)
  .on(getCardUsersEffect.done, () => false)
  .on(addUsersInCardEffect.pending, (_, data) => data)
  .on(addUsersInCardEffect.fail, () => false)
  .on(addUsersInCardEffect.done, () => false)
  .on(deleteUserInCardEffect.pending, (_, data) => data)
  .on(deleteUserInCardEffect.fail, () => false)
  .on(deleteUserInCardEffect.done, () => false)
  .reset(clearEvent);

const $errorStore = createStore<string | null>(null)
  .on(getCardUsersEffect, () => null)
  .on(getCardUsersEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(getCardUsersEffect.done, () => null)
  .on(addUsersInCardEffect, () => null)
  .on(addUsersInCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(addUsersInCardEffect.done, () => null)
  .on(deleteUserInCardEffect, () => null)
  .on(deleteUserInCardEffect.fail, (_, data) => i18n.t(`errors.api.${data.error.message}`, data.error.message))
  .on(deleteUserInCardEffect.done, () => null)
  .reset(clearEvent);

$errorStore.watch((x) => $notificationStore.events.setError(x));

export const $cardUsersStore = {
  events: {
    getCardUsers: ApiUtils.wrapperApi(getCardUsersEffect, RequestStrategyEnum.Debounce),
    addUsersInCard: addUsersInCardEffect,
    deleteUserInCard: ApiUtils.wrapperApi(deleteUserInCardEffect, RequestStrategyEnum.Debounce),
    clear: clearEvent,
  },
  store: combine({
    data: $dataStore,
    loading: $loadingStore,
    error: $errorStore,
  }),
};
